
import { supabase } from "@/integrations/supabase/client";

export interface InternalMessage {
  id: string;
  sender_id: string;
  sender_name: string;
  sender_email: string;
  subject: string;
  content: string;
  priority: "low" | "normal" | "high" | "urgent";
  status: "new" | "read" | "in_progress" | "resolved";
  created_at: string;
  updated_at: string;
}

/**
 * Envoyer un message interne
 */
export const sendInternalMessage = async (
  subject: string,
  content: string,
  priority: "low" | "normal" | "high" | "urgent" = "normal"
): Promise<boolean> => {
  console.log("=== DÉBUT sendInternalMessage ===");
  
  try {
    // Récupérer l'utilisateur connecté via Supabase Auth
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    console.log("Utilisateur récupéré:", user);
    
    if (authError || !user) {
      console.error("Erreur d'authentification:", authError);
      return false;
    }

    // Récupérer le profil de l'utilisateur
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('name, email')
      .eq('id', user.id)
      .single();

    if (profileError || !profile) {
      console.error("Erreur lors de la récupération du profil:", profileError);
      return false;
    }

    const messageData = {
      sender_id: user.id,
      sender_name: profile.name,
      sender_email: profile.email,
      subject,
      content,
      priority,
      status: 'new' as const
    };

    console.log("Données du message à envoyer:", messageData);

    const { data, error } = await supabase
      .from('internal_messages')
      .insert(messageData)
      .select();

    if (error) {
      console.error("Erreur Supabase détaillée:", error);
      return false;
    }

    console.log("Message inséré avec succès:", data);
    console.log("=== FIN sendInternalMessage (SUCCÈS) ===");
    return true;
  } catch (error) {
    console.error("Erreur lors de l'envoi du message:", error);
    console.log("=== FIN sendInternalMessage (ERREUR) ===");
    return false;
  }
};

/**
 * Récupérer tous les messages (pour les admins)
 */
export const getAllMessages = async (): Promise<InternalMessage[]> => {
  try {
    const { data, error } = await supabase
      .from('internal_messages')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error("Erreur lors de la récupération des messages:", error);
      return [];
    }

    return (data || []).map(item => ({
      ...item,
      priority: item.priority as "low" | "normal" | "high" | "urgent",
      status: item.status as "new" | "read" | "in_progress" | "resolved"
    }));
  } catch (error) {
    console.error("Erreur lors de la récupération des messages:", error);
    return [];
  }
};

/**
 * Récupérer les messages de l'utilisateur actuel
 */
export const getUserMessages = async (): Promise<InternalMessage[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return [];
    }

    const { data, error } = await supabase
      .from('internal_messages')
      .select('*')
      .eq('sender_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error("Erreur lors de la récupération des messages utilisateur:", error);
      return [];
    }

    return (data || []).map(item => ({
      ...item,
      priority: item.priority as "low" | "normal" | "high" | "urgent",
      status: item.status as "new" | "read" | "in_progress" | "resolved"
    }));
  } catch (error) {
    console.error("Erreur lors de la récupération des messages utilisateur:", error);
    return [];
  }
};

/**
 * Mettre à jour le statut d'un message (pour les admins)
 */
export const updateMessageStatus = async (
  messageId: string,
  status: "new" | "read" | "in_progress" | "resolved"
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('internal_messages')
      .update({ 
        status,
        updated_at: new Date().toISOString()
      })
      .eq('id', messageId);

    if (error) {
      console.error("Erreur lors de la mise à jour du statut:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Erreur lors de la mise à jour du statut:", error);
    return false;
  }
};

/**
 * Supprimer un message interne (pour les admins)
 */
export const deleteInternalMessage = async (messageId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('internal_messages')
      .delete()
      .eq('id', messageId);

    if (error) {
      console.error("Erreur lors de la suppression du message:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Erreur lors de la suppression du message:", error);
    return false;
  }
};
